import { MouseEventHandler } from "react";

import { useTheme } from "../../../../context/theme";
import { DISABLED_OPACITY } from "../../../../styles/colors/consts";
import { Icon } from "../../../content";
import { Box } from "../../../layout";

type Props = {
  onClick: MouseEventHandler;
  disabled?: boolean;
  searchable?: boolean;
  menuIsOpen?: boolean;
  ariaLabel?: string;
  children: React.ReactNode;
};

export default function DropdownControl({
  onClick,
  disabled,
  searchable,
  ariaLabel,
  children,
  menuIsOpen
}: Props) {
  const theme = useTheme();

  const { controlStyles, iconStyles } = useCustomStyles({
    disabled,
    menuIsOpen,
    searchable
  });

  return (
    <Box
      type="button"
      component="button"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      aria-label={ariaLabel}
      onClick={onClick}
      borderRadius="small"
      width="100%"
      minHeight="38px"
      paddingX="large"
      paddingY="small"
      cursor="pointer"
      opacity={disabled ? DISABLED_OPACITY : "1"}
      focusStyles={{
        outline: "0",
        borderColor: theme["primary-high-content"]
      }}
      {...controlStyles}
    >
      {children}
      <Box {...iconStyles} marginLeft="large">
        {searchable ? (
          <Icon
            accessibilityLabel="search"
            tone="neutral"
            size="medium"
            iconName="search"
          />
        ) : (
          <Icon
            accessibilityLabel="open"
            tone="neutral"
            size="medium"
            iconName="chevronDown"
          />
        )}
      </Box>
    </Box>
  );
}

function useCustomStyles({
  disabled,
  menuIsOpen,
  searchable
}: {
  disabled?: boolean;
  menuIsOpen?: boolean;
  searchable?: boolean;
}) {
  const theme = useTheme();

  return {
    controlStyles: {
      color: theme["neutral-medium-content"],
      borderStyle: "solid",
      borderWidth: "xxxsmall" as const,
      borderColor:
        !disabled && menuIsOpen
          ? theme["primary-high-content"]
          : theme["neutral-high-content"],
      backgroundColor: theme.foreground
    },
    iconStyles: {
      ...(menuIsOpen &&
        !searchable && {
          transition: "transform 0.2s ease",
          transform: "rotate(180deg)"
        })
    }
  };
}
